import React, { useEffect, useMemo, useState } from 'react';
import Select, { SingleValue } from "react-select";
import { DataSelectType } from '../models/DataSelectType';
import { useLanguageStore } from '../store/useLanguageStore';
import { useDarkModeStore } from '../store/useDarkModeStore';
import { darkModeBackgroudInputs } from '../utils/constants';

interface DinamicSelectCompProps<T> {
    resetTrigger: number;
    fieldDisplayName: string;
    label?: string;
    optionsData: T[] | undefined;
    companyId?: string | undefined;
    menuPlacement?: "top" | "bottom";
    filterData: DataSelectType[] | undefined;
    setFilterData: React.Dispatch<React.SetStateAction<DataSelectType[] | undefined>>;
}

export const DinamicSelectComp = <T extends { id?: string; title: string, name: string }>({
    filterData,
    optionsData,
    resetTrigger,
    fieldDisplayName,
    label,
    menuPlacement = "bottom",
    setFilterData,
    companyId = undefined,
}: DinamicSelectCompProps<T>) => {

    const field = fieldDisplayName === 'PurchaseOrderStatus' ? fieldDisplayName : `${fieldDisplayName}Id`;
    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();

    const defaultOption = useMemo(() => ({
        label: language['Select'],
        value: 'NotSelected',
        field
    }), [language, field]);

    const [searchTerm, setSearchTerm] = useState<DataSelectType>(defaultOption);
    const [selectItems, setSelectItems] = useState<DataSelectType[]>([defaultOption]);

    useEffect(() => {
        setSearchTerm(defaultOption);
    }, [resetTrigger, defaultOption]);

    useEffect(() => {
        if (optionsData) {
            const result = optionsData.map(item => ({
                label: item.title ?? item.name,
                value: item.id ?? '',
                field
            }));
            setSelectItems([defaultOption, ...result]);
        }
    }, [optionsData, field, language, defaultOption]);

    useEffect(() => {
        if (filterData) {
            const foundTerm = filterData.find(item => item.field === field && item.value !== 'NotSelected');
            if (foundTerm) setSearchTerm(foundTerm);
        }
    }, [filterData, field]);

    useEffect(() => {
        if (searchTerm.field === 'LocationId' || searchTerm.field === 'CustomerId') setSearchTerm(defaultOption);
    }, [companyId]);

    const handleSelect = (selectedOption: SingleValue<{ label: string; value: string }>) => {
        let upFilterData = filterData ? [...filterData] : [];

        if (!selectedOption) {
            upFilterData = upFilterData.filter(item => item.field !== field);
            setSearchTerm(defaultOption);
        } else {
            setSearchTerm({ label: selectedOption.label, value: selectedOption.value, field });
            if (selectedOption.value === 'NotSelected') { upFilterData = upFilterData.filter(item => item.field !== field); }
            else if (selectedOption.value || parseInt(selectedOption.value) == 0) {
                const existingIndex = upFilterData.findIndex(item => item.field === field);
                if (existingIndex !== -1) {
                    upFilterData[existingIndex].value = selectedOption.value;
                    upFilterData[existingIndex].label = selectedOption.label;
                }
                else upFilterData.push({ label: selectedOption.label, value: selectedOption.value, field });
            }
        }
        setFilterData(upFilterData);
    };

    return (
        <div className='d-flex flex-column gap-1' title={language['FilterBased'] + language[fieldDisplayName]}>
            <label
                htmlFor={field}
                className={`fw-bold ${darkMode ? 'text-white' : 'text-muted'}`}
                style={{ fontSize: 12 }}
            >
                {label ?? language[fieldDisplayName]}
            </label>
            <Select
                id={field}
                isClearable
                styles={{
                    menu: (provided) => ({ ...provided, zIndex: 11 }),
                    container: (provided) => ({ ...provided, position: 'relative', zIndex: 11 }),
                    menuPortal: (base) => ({ ...base, zIndex: 11 }),
                    singleValue: (provided) => ({
                        ...provided,
                        color: darkMode ? 'white' : 'black',
                    }),
                    control: (provided) => ({
                        ...provided,
                        backgroundColor: darkMode ? darkModeBackgroudInputs : "white",
                        borderColor: darkMode ? darkModeBackgroudInputs : "initial",
                    }),
                }}
                value={searchTerm}
                options={selectItems}
                menuPlacement={menuPlacement}
                menuPortalTarget={document.body}
                placeholder={language['Select']}
                onChange={handleSelect}
            />
        </div>
    );
}

export default DinamicSelectComp;
